<template>
  <NuxtLayout>
    <NuxtLoadingIndicator color="#9c2b32" />
    <AppWideBanners v-if="route.name !== 'the-board'" />
    <NuxtPage />
    <template #error="{ error }">
      <ContainerBasic>
        <UCard class="my-20 flex flex-col gap-16 text-center">
          <HeadingBasic :size="3">Ooops! Looks like we've encountered an error.</HeadingBasic>
          <p>{{ error }}</p>
        </UCard>
      </ContainerBasic>
    </template>
    <NuxtErrorBoundary @error="logSomeError"> </NuxtErrorBoundary>
  </NuxtLayout>
  <UNotifications />
  <!-- <div class="fixed bottom-4 right-4 z-[10]">
    <select v-model="$colorMode.preference">
      <option value="system">System</option>
      <option value="light">Light</option>
      <option value="dark">Dark</option>
    </select>
  </div> -->
  <VueQueryDevtools />
</template>

<script lang="ts" setup>
import type { Database } from '~/types';
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
const route = useRoute();
const profileStore = useProfileStore();
const { profile } = profileStore;
const { hasAccess, hasPortalPass, hasCurrentPortalPass, hasTrialed } = storeToRefs(profileStore);

const nuxtApp = useNuxtApp();
const user = useSupabaseUser();

const { posthog } = usePosthog();

const PAGE_TITLE = `Home`;
const SITE_TITLE_ENDING = ` | 64Analytics.com`;
const SITE_TITLE = `${PAGE_TITLE}${SITE_TITLE_ENDING}`;

useHead({
  titleTemplate: (title) => {
    return title ? `${title}${SITE_TITLE_ENDING}` : SITE_TITLE;
  },
  script: [
    {
      src: 'https://kit.fontawesome.com/cf77921f49.js',
      crossorigin: 'anonymous',
    },
  ],
});

const logSomeError = (e: Error) => {
  if (import.meta.client) {
    posthog?.captureException(e);
  }
  console.error(e);
};

watch(profile, () => {
  setIntercomSettings();
});

const setIntercomSettings = () => {
  if (!import.meta.client || typeof window === 'undefined') return false;

  (window as any).intercomSettings = profile?.email
    ? {
        api_base: 'https://api-iam.intercom.io',
        app_id: 'fns3im04',
        name: profile.first_name + ' ' + profile.last_name,
        user_id: profile.id,
        email: profile.email,
        created_at: profile.created_at,
      }
    : {
        api_base: 'https://api-iam.intercom.io',
        app_id: 'fns3im04',
      };
};

onMounted(() => {
  // Initialize profile using the store's fetchProfile method
  if (user.value) {
    profileStore.fetchProfile(user.value);
  }

  if (!import.meta.client || typeof window === 'undefined') return;

  setIntercomSettings();

  // We pre-filled your app ID in the widget URL: 'https://widget.intercom.io/widget/fns3im04'
  (function () {
    var w = window;
    var ic = w.Intercom;
    if (typeof ic === 'function') {
      ic('reattach_activator');
      ic('update', w.intercomSettings);
    } else {
      var d = document;
      var i = function () {
        i.c(arguments);
      };
      i.q = [];
      i.c = function (args) {
        i.q.push(args);
      };
      w.Intercom = i;
      var l = function () {
        var s = d.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = 'https://widget.intercom.io/widget/fns3im04';
        var x = d.getElementsByTagName('script')[0];
        x.parentNode.insertBefore(s, x);
      };
      if (document.readyState === 'complete') {
        l();
      } else if (w.attachEvent) {
        w.attachEvent('onload', l);
      } else {
        w.addEventListener('load', l, false);
      }
    }
  })();
});

// Initialize profile when user changes using the store's fetchProfile method
watchEffect(() => {
  if (user.value && (!profile.email || profile.email !== user.value.email)) {
    profileStore.fetchProfile(user.value);
  }
});

// const onloadCallback = () => {
//   grecaptcha.ready(function () {
//     grecaptcha
//       .execute(config.public.googleRecaptchaSiteKey, { action: 'submit' })
//       .then(function (token) {
//         // Add your logic to submit to your backend server here.
//         console.log('grecaptcha!!!');
//         console.log(token);
//       });
//   });
// };
</script>
